import { ContentTab } from '../../types.ts/general';

/** Layout mode for Content Studio tab containers. */
export type ScrollMode = 'page' | 'contained';

/**
 * Tabs whose internal layout depends on a height-bounded ancestor
 * (legacy `flex: 1; min-height: 0; overflow: auto` chain).
 *
 * For these tabs, the outer shell uses `height: 100vh; overflow: hidden`
 * so the flex chain resolves correctly — no document scroll, single
 * inner scrollbar. All other tabs use page-mode (natural document scroll
 * with sticky headers).
 */
export const CONTAINED_TABS: ReadonlySet<ContentTab> = new Set([
  ContentTab.talking_point_studio,
  ContentTab.media,
  ContentTab.transcript_editor,
  ContentTab.transcript_speakers,
  ContentTab.blog,
  ContentTab.social_posts,
]);

/** Parse a URL search param value into a ContentTab, or undefined if unrecognised. */
/**
 * PLAT-11877 — tab names that links may still carry from before a rename;
 * the `tab=` value doubles as the tab's label, so a rename would otherwise
 * break every bookmark and review link made before it.
 */
const LEGACY_TAB_VALUES: Record<string, ContentTab> = {
  'Transcript & Speakers': ContentTab.transcript_speakers,
};

export function parseContentTab(value: string | null): ContentTab | undefined {
  if (!value) return undefined;
  return (
    Object.values(ContentTab).find(tab => tab === value) ??
    LEGACY_TAB_VALUES[value]
  );
}

/**
 * PLAT-11991 — the transcript tab's display name follows the showcase: with
 * supported languages it is "Transcript & Languages", without them it is
 * "Transcript" (PLAT-12076's wording) — the same rule that shows or hides the language
 * controls inside (`TranslationToggle` renders nothing for an empty list), so
 * the tab never promises what its toolbar does not offer. The enum value (the
 * URL `tab=` key) does not change; see `LEGACY_TAB_VALUES`.
 */
export const TRANSCRIPT_TAB_LABELS = {
  withLanguages: 'Transcript & Languages',
  withoutLanguages: 'Transcript',
} as const;

export function transcriptTabLabel(hasLanguages: boolean): string {
  return hasLanguages
    ? TRANSCRIPT_TAB_LABELS.withLanguages
    : TRANSCRIPT_TAB_LABELS.withoutLanguages;
}

/** A tab's display name — the enum value, except the transcript tab's. */
export function contentTabLabel(
  tab: ContentTab,
  hasLanguages: boolean,
): string {
  return tab === ContentTab.transcript_speakers
    ? transcriptTabLabel(hasLanguages)
    : tab;
}
